package dev.lingvo.site.pages.app.settings.billing

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.BillingDocumentDto
import dev.lingvo.shared.BillingOverviewDto
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.Async
import dev.lingvo.site.components.billing.PortalButton
import dev.lingvo.site.ds.ErrorState
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LAsyncContent
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LEmptyState
import dev.lingvo.site.ds.LEmptyStateIcon
import dev.lingvo.site.ds.LTable
import dev.lingvo.site.ds.LTableBody
import dev.lingvo.site.ds.LTableCell
import dev.lingvo.site.ds.LTableHead
import dev.lingvo.site.ds.LTableHeaderCell
import dev.lingvo.site.ds.LTableRow
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.dateShort
import dev.lingvo.site.ds.tabularNumbers
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.pages.app.settings.loadWorkspaceProtectedResource
import dev.lingvo.site.pages.app.settings.reloadWorkspaceProtectedResource
import dev.lingvo.site.pages.app.settings.rememberWorkspaceAuthorityState
import dev.lingvo.site.state.WorkspaceActionAuthority
import dev.lingvo.site.state.WorkspaceActionClaim
import dev.lingvo.site.state.WorkspacePrivilege
import dev.lingvo.site.state.WorkspaceRequestContext
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

/**
 * What has been charged, and when.
 *
 * Admins see the list; only an owner gets a document. **A Paddle invoice carries the owner's personal
 * billing address**, which is not something an admin is entitled to just because they can raise the
 * bill — and it is the kind of detail nobody thinks about until it has already been shown.
 *
 * Amounts come from the cache the processor writes on `transaction.completed`, so this page never
 * waits on a third party to render.
 */
@Page("/app/settings/billing/invoices")
@Layout(".components.layouts.AppLayout")
@Composable
fun InvoicesPage() {
    val workspaceContext = WorkspaceRequestContext.current()
    val workspace = workspaceContext?.workspace
    val authority = WorkspaceActionAuthority.resolve(workspaceContext)
    // A retry that retries. `ErrorState` draws its button whenever `onRetry` is non-null, so
    // `{ }` produced a visible control that does nothing — on a page reached precisely when
    // the network is unreliable, whose only other exit is a full browser reload.
    var refreshKey by rememberWorkspaceAuthorityState(workspaceContext) { mutableStateOf(0) }

    Column(Modifier.fillMaxWidth().gap(Space.XL)) {
        PageHeader(
            t("app.settings.billing.invoices.invoices", "Invoices"),
            tf(
                "app.settings.billing.invoices.every_charge_on_workspace",
                "Every charge on {workspace}.",
                "workspace" to (
                    workspace?.name ?: t("app.settings.billing.invoices.this_workspace", "this workspace")
                ),
            ),
        )

        if (workspaceContext == null || !authority.canReadBilling) {
            LAlert(
                title = t("app.settings.billing.invoices.invoices_are_managed_by_an_owner", "Invoices are managed by an owner or admin"),
                description = t("app.settings.billing.invoices.billing_amounts_and_documents_are_not", "Billing amounts and documents are not available for your role in this workspace."),
                variant = LAlertVariant.INFO,
            )
            return@Column
        }

        // A refresh reloads underneath the invoice list rather than emptying it. As a key the
        // counter discarded the rows before the request went out, so pressing refresh on a billing
        // page blanked the very table somebody was reading a number off.
        val invoices = reloadWorkspaceProtectedResource(
            context = workspaceContext,
            privilege = WorkspacePrivilege.BILLING_READ,
            reloadToken = refreshKey,
            requestKeys = listOf("invoices"),
            transport = ApiClient::workspaceInvoices,
        )
        // Read from the same cache the list comes from, so the page still never waits on Paddle. It
        // exists only to keep the empty state from asserting "Free" at somebody who has just paid.
        val billingReloadable = reloadWorkspaceProtectedResource(
            context = workspaceContext,
            privilege = WorkspacePrivilege.BILLING_READ,
            reloadToken = refreshKey,
            requestKeys = listOf("billing"),
            transport = ApiClient::workspaceBilling,
        )
        val billing = billingReloadable.state

        if (!authority.canUseOwnerBilling) {
            LAlert(
                t("app.settings.billing.invoices.you_can_see_the_amounts_not", "You can see the amounts, not the documents"),
                t("app.settings.billing.invoices.an_invoice_carries_the_owners_personal_2", "An invoice carries the owner's personal billing address, so only the owner can " +
                    "fetch the document itself."),
                variant = LAlertVariant.INFO,
            )
        }

        LAsyncContent(
            invoices.state,
            rows = 3,
            loadingLabel = t("app.settings.billing.invoices.loading_invoices", "Loading invoices"),
            // An unreadable list says nothing about whether a payment completed.
            failure = {
                ErrorState(
                    invoicesUnreadableNote(),
                    onRetry = { refreshKey++ },
                )
            },
        ) { loaded ->
            val documents = loaded.orEmpty()
            if (documents.isEmpty()) {
                EmptyInvoices(billing)
            } else {
                InvoiceTable(documents)
                // Where the PDFs actually are. The amounts above come from our own cache; the
                // documents themselves have never been ours to serve, and until now the page
                // said an owner could download one without offering any way to.
                if (authority.canUseOwnerBilling) {
                    PortalButton(
                        WorkspaceActionClaim.capture(workspaceContext, WorkspacePrivilege.OWNER_BILLING),
                        invoicePortalLabel(),
                        variant = LButtonVariant.SECONDARY,
                        invoices = true,
                    )
                }
            }
        }
    }
}

/**
 * An empty cached list is not proof of either payment or non-payment. A subscription or checkout
 * marker does not identify a completed transaction, and an unavailable overview stays unknown.
 */
@Composable
private fun EmptyInvoices(billing: Async<BillingOverviewDto?>) {
    val ctx = rememberPageContext()
    val overview = (billing as? Async.Success)?.value

    when {
        // Keep the billing exit for a linked subscription or checkout awaiting its outcome.
        overview != null && (overview.hasSubscription || overview.pendingCheckoutStartedAt != null) ->
            LEmptyState(
                t("app.settings.billing.invoices.no_recorded_invoice_documents", "No invoice documents are recorded yet"),
                t("app.settings.billing.invoices.empty_list_not_payment_confirmation", "An empty invoice list does not confirm whether a payment was completed. Check billing for the latest status. Do not pay again before checking."),
                Modifier.fillMaxWidth(),
                LEmptyStateIcon.INSIGHTS,
            ) {
                LButton(t("app.settings.billing.invoices.back_to_billing", "Back to billing"), { ctx.router.tryRoutingTo("/app/settings/billing") })
            }

        overview == null -> LEmptyState(
            t("app.settings.billing.invoices.no_invoices_to_show_yet", "No invoices to show yet"),
            t("app.settings.billing.invoices.billing_status_unavailable", "We couldn't confirm this workspace's billing status. Check billing or contact support@lingvo.dev before starting another checkout."),
            Modifier.fillMaxWidth(),
            LEmptyStateIcon.INSIGHTS,
        ) {}

        else -> LEmptyState(
            t("app.settings.billing.invoices.no_invoices_yet", "No invoices yet"),
            t("app.settings.billing.invoices.empty_recorded_list", "No invoice documents are recorded for this workspace. Check billing for your current plan and payment status."),
            Modifier.fillMaxWidth(),
            LEmptyStateIcon.INSIGHTS,
        ) {}
    }
}

@Composable
private fun InvoiceTable(rows: List<BillingDocumentDto>) {
    LTable(caption = t("app.settings.billing.invoices.invoices", "Invoices")) {
        LTableHead {
            LTableRow {
                // Spelled out rather than looped over a `listOf`: a literal inside a list is
                // invisible to the catalogue extractor, so these four headers were the last
                // untranslated text on the page.
                LTableHeaderCell { Text(t("app.settings.billing.invoices.invoice", "Invoice")) }
                LTableHeaderCell { Text(t("app.settings.billing.invoices.date", "Date")) }
                LTableHeaderCell { Text(t("app.settings.billing.invoices.amount", "Amount")) }
                LTableHeaderCell { Text(t("app.settings.billing.invoices.status", "Status")) }
            }
        }
        LTableBody {
            rows.forEach { row ->
                LTableRow {
                    LTableCell { Text(row.invoiceNumber ?: "—") }
                    // `date/short`, not the raw ISO cut this column used to show. A statement
                    // somebody is reconciling against their bank is scanned, and "2026-08-18"
                    // beside "$19.00" reads as a reference number rather than a date.
                    LTableCell { Text(row.billedAt?.let(::dateShort) ?: "—") }
                    // The currency actually charged, formatted by the server. A locally rendered
                    // "$" on a euro invoice is a number nobody can reconcile with a bank statement.
                    LTableCell { P(Modifier.tabularNumbers().margin(0.px).toAttrs()) { Text(row.amount.display) } }
                    LTableCell { Text(row.status) }
                }
            }
        }
    }
}

/** What a failed read of the invoices says. `ErrorState`'s message is positional copy. */
internal fun invoicesUnreadableNote(): String = tr(
    "app.settings.billing.invoices.invoice_list_unavailable",
    "We can't read your invoices right now. Retry to check the recorded billing documents.",
)

/**
 * The one control on this page that leads to the actual PDFs.
 *
 * `PortalButton`'s label is its second positional argument and the extractor does not follow it, so
 * the only way off this page towards a document was labelled in English for every locale.
 */
internal fun invoicePortalLabel(): String = tr(
    "app.settings.billing.invoices.get_invoice_documents_from_the_billing",
    "Get invoice documents from the billing portal",
)
